import { addDays, differenceInDays, subDays } from 'date-fns';

import { ValueChangeCombinedParams, ValueChangeParams } from '../../types';

interface useDateValidationProps {
    currentValues: ValueChangeCombinedParams;
    minStart: Date;
    minRange: number;
    maxRange: number;
    callbacks: {
        triggerStartDateSelection: () => void;
        triggerEndDateSelection: () => void;
        triggerCloseDateSelection: (value: ValueChangeParams) => void;
        onValueChange: (value: ValueChangeCombinedParams) => void;
    };
}

export const useDateValidation = (config: useDateValidationProps) => {
    const { currentValues, minStart, minRange, maxRange, callbacks } = config;

    const returnValue = currentValues.to
        ? differenceInDays(currentValues.to, currentValues.from)
        : 0;
    const delta = returnValue < minRange ? minRange : returnValue;

    function validateFromStartDate(selectedDate: Date) {
        if (selectedDate < minStart) {
            // START-DATE < MIN-START
            // -> SET START-DATE TO MIN-START
            // -> SET END-DATE TO (START-DATE + DELTA)
            callbacks.onValueChange({
                from: minStart,
                to: undefined,
            });
        } else {
            callbacks.onValueChange({
                from: selectedDate,
                to: undefined,
            });
        }
        callbacks.triggerEndDateSelection();
    }

    function validateFromEndDate(selectedDate: Date) {
        if (selectedDate <= currentValues.from) {
            // END-DATE < START-DATE
            // -> SET END-DATE TO SELECTED-DATE
            // -> SET START DATE TO (END-DATE - DELTA)
            const newStartDate = delta && subDays(selectedDate, delta);
            if (newStartDate && newStartDate < minStart) {
                // always make sure that new start date is not earlier than min start date
                callbacks.onValueChange({
                    from: minStart,
                    to:
                        differenceInDays(minStart, selectedDate) < minRange // delta should not be smaller than minRange
                            ? addDays(selectedDate, minRange)
                            : selectedDate,
                });
                () => config.callbacks.triggerStartDateSelection();
            } else if (newStartDate) {
                const newDateSelection = {
                    from: newStartDate,
                    to: selectedDate,
                };
                callbacks.onValueChange(newDateSelection);
                callbacks.triggerCloseDateSelection(newDateSelection);
            }
        } else if (delta && selectedDate < minStart) {
            // END-DATE < MIN-START
            // -> SET START-DATE TO MIN-START
            // -> SET END-DATE TO (START-DATE + DELTA)
            const newDateSelection = {
                from: minStart,
                to: addDays(minStart, delta),
            };
            callbacks.onValueChange(newDateSelection);
            callbacks.triggerCloseDateSelection(newDateSelection);
        } else {
            if (delta && delta > maxRange) {
                // DELTA > MAX-RANGE
                // -> SET END-DATE TO SELECTED-DATE
                // -> SET START-DATE TO (END-DATE - MAX-RANGE)
                const newDateSelection = {
                    from: subDays(selectedDate, maxRange),
                    to: selectedDate,
                };
                callbacks.onValueChange(newDateSelection);
                callbacks.triggerCloseDateSelection(newDateSelection);
            } else if (delta && delta < minRange) {
                // DELTA < MIN-RANGE
                // -> SET END-DATE TO SELECTED-DATE
                // -> SET START-DATE TO (END-DATE - MIN-RANGE)
                const newStartDate = subDays(selectedDate, delta);
                if (newStartDate < minStart) {
                    // always make sure that new start date is not earlier than min start date
                    callbacks.onValueChange({
                        from: minStart,
                        to:
                            differenceInDays(minStart, selectedDate) < minRange // delta should not be smaller than minRange
                                ? addDays(selectedDate, minRange)
                                : selectedDate,
                    });
                    () => config.callbacks.triggerStartDateSelection();
                } else {
                    const newDateSelection = {
                        from: newStartDate,
                        to: selectedDate,
                    };
                    callbacks.onValueChange(newDateSelection);
                    callbacks.triggerCloseDateSelection(newDateSelection);
                }
            } else {
                if (
                    differenceInDays(selectedDate, currentValues.from) <
                    minRange
                ) {
                    const newStartDate = subDays(currentValues.from, minRange);
                    // make sure we always keep the min range between start and end date
                    if (newStartDate < minStart) {
                        const newDateSelection = {
                            from: minStart,
                            to: selectedDate,
                        };
                        callbacks.onValueChange(newDateSelection);
                        callbacks.triggerCloseDateSelection(newDateSelection);
                    } else {
                        const newDateSelection = {
                            from: newStartDate,
                            to: selectedDate,
                        };
                        callbacks.onValueChange(newDateSelection);
                        callbacks.triggerCloseDateSelection(newDateSelection);
                    }
                } else {
                    const newDateSelection = {
                        from: currentValues.from,
                        to: selectedDate,
                    };
                    callbacks.onValueChange(newDateSelection);
                    callbacks.triggerCloseDateSelection(newDateSelection);
                }
            }
        }
    }

    return { validateFromStartDate, validateFromEndDate, range: currentValues };
};
